import { hits, refinementList, currentRefinements, clearRefinements, configure, searchBox } from 'instantsearch.js/es/widgets';
import { connectStats, connectPagination } from 'instantsearch.js/es/connectors';
import { ConfigureWidgetParams } from 'instantsearch.js/es/widgets/configure/configure';
import { slugify } from '../../utils/utils';


const decodeHtmlEntities = (str: string): string => {
  const doc = new DOMParser().parseFromString(str, 'text/html');
  return doc.documentElement.textContent ?? str;
};

const refinementstemplateRenderer = ({ label, value, count, isRefined }: {
  label: string;
  value: string;
  count: number;
  isRefined: boolean
})=> {
  const slug = slugify(value);
  return `
      <label class="ais-RefinementList-label" for="ais-Refinement-${slug}">
        <input id="ais-Refinement-${slug}" class="ais-RefinementList-checkbox" type="checkbox" value="${value}" ${isRefined ? 'checked' : ''}  aria-checked="${isRefined}" />
        <span class="ais-RefinementList-labelText">${label}</span>
        <span class="ais-RefinementList-count">${count}</span>
      </label>
    `;
};

// HITS
const getHits = (container: HTMLElement, cssClasses = {
  root: 'posts-grid',
  list: 'posts-grid__wrapper',
}, templates) => {

  return hits({
    container,
    cssClasses,
    templates,
  });
};


// REFINEMENTS
const getRefinements = (container: HTMLElement, attribute: string, templateRenderer: any, excludedValues: (string | number)[] = []) => {
  return refinementList({
    container,
    attribute,
    sortBy: ['count:desc', 'name:asc'],
    templates: {
      item: templateRenderer,
    },
    transformItems(items) {
      if (!excludedValues.length) return items;
      return items.filter(item => !excludedValues.includes(item.value));
    },
  });
};

// CURRENT REFINEMENTS
const getCurrentRefinements = (container: HTMLElement, transformItems) => {
  const conf = { container };
  if (transformItems) conf.transformItems = transformItems;

  return currentRefinements(conf);
};

// CLEAR REFINEMENTS
const getClearRefinements = (container: HTMLElement) => {
  return clearRefinements({
    container,
    templates: {
      resetLabel({ hasRefinements }, { html }) {
        return html`<span>${hasRefinements ? 'Tout effacer' : 'Aucun filtre actif'}</span>`;
      },
    },
  });
};

// STATS
const getStats = (container: HTMLElement) => {
  return connectStats(({ nbHits }) => {
    container.textContent = nbHits ? `(${nbHits})` : '';
  })({});
};

// PAGINATION
const getPagination = (container: HTMLElement) => {
  let refineRef: ((page: number) => void) | null = null;

  container.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest<HTMLElement>('[data-page]');
    const headerHeight = document.querySelector('#main-header')?.clientHeight || 0;
    const targetPosition = document.getElementById('search-results-top')?.getBoundingClientRect().top || 0;

    if (link && refineRef) {
      e.preventDefault();
      refineRef(Number(link.dataset.page));

      window.scrollTo({
        top: targetPosition + window.pageYOffset - headerHeight,
        behavior: 'smooth',
      });
    }
  });

  return connectPagination(({ pages, currentRefinement, nbPages, refine }) => {
    refineRef = refine;

    if (nbPages <= 1) {
      container.innerHTML = '';
      return;
    }

    const prevDisabled = currentRefinement === 0;
    const nextDisabled = currentRefinement >= nbPages - 1;

    container.innerHTML = `
        <ul class="ais-Pagination-list">
          <li class="ais-Pagination-item ais-Pagination-item--previousPage${prevDisabled ? ' ais-Pagination-item--disabled' : ''}">
            <a class="ais-Pagination-link" 
              ${prevDisabled ? 'aria-disabled="true"' : `href="#search-results-top" data-page="${currentRefinement - 1}"`}
              aria-label="Page précédente">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <g clip-path="url(#clip0_2624_368)">
                  <path d="M7.21867 8L10.5187 11.3L9.576 12.2427L5.33333 8L9.576 3.75733L10.5187 4.7L7.21867 8Z" fill="black"/>
                </g>
                <defs>
                  <clipPath id="clip0_2624_368">
                    <rect width="16" height="16" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              </a>
          </li>
          ${pages.map((page: number) => `
            <li class="ais-Pagination-item ais-Pagination-item--page${page === currentRefinement ? ' ais-Pagination-item--selected' : ''}">
              <a class="ais-Pagination-link"${page === currentRefinement ? '' : ` href="#search-results-top" data-page="${page}"`}
                aria-label="Page ${page + 1}"${page === currentRefinement ? ' aria-current="page"' : ''}>${page + 1}</a>
            </li>
          `).join('')}
          <li class="ais-Pagination-item ais-Pagination-item--nextPage${nextDisabled ? ' ais-Pagination-item--disabled' : ''}">
            <a class="ais-Pagination-link" 
              ${nextDisabled ? 'aria-disabled="true"' : `href="#search-results-top" data-page="${currentRefinement + 1}"`}
              aria-label="Page suivante">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <g clip-path="url(#clip0_2624_368)">
                  <path d="M7.21867 8L10.5187 11.3L9.576 12.2427L5.33333 8L9.576 3.75733L10.5187 4.7L7.21867 8Z" fill="black"/>
                </g>
                <defs>
                  <clipPath id="clip0_2624_368">
                    <rect width="16" height="16" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              </a>
          </li>
        </ul>
    `;
  })({ padding: 2 });
};

// CONFIGURE
const getConfigure = (params: ConfigureWidgetParams) => {
  return configure(params);
};

// SEARCHBOX
const getSearchBox = (container: string | HTMLElement, placeholder?: string, ) => {
  return searchBox({
    container,
    placeholder,
    showReset: false,
    showSubmit: true,
    showLoadingIndicator: false,
  });
};

export {
  decodeHtmlEntities,
  refinementstemplateRenderer,
  getHits,
  getRefinements,
  getCurrentRefinements,
  getClearRefinements,
  getConfigure,
  getStats,
  getPagination,
  getSearchBox,
};